import React, { useState } from 'react';
import classNames from 'classnames';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { Icon } from '@frontend/shared-ui-kit/icon';
import { Typography } from '@frontend/shared-ui-kit/typography';
import { ImageWithFallback } from '@frontend/shared-ui-kit/image';
import { formatValue } from '@frontend/shared-utils';
import { LinkCreationModal } from '@frontend/howl-web-app/feature-publisher';
import { useActions, useAppState } from '@frontend/howl-web-app/overmind';

const Product = ({
  merchName,
  product,
  canMakeLink,
  imageSize,
  textSize,
  noMargin,
  type,
  className,
  campaign,
  fallbackImage,
  linkout,
  bordered = true,
}: {
  product: any;
  merchName?: string;
  canMakeLink?: boolean;
  imageSize?: string;
  textSize?: string;
  noMargin?: boolean;
  fallbackImage?: string;
  bordered?: boolean;

  type?: 'brand' | 'campaign' | 'campaigns' | null;
  className?: string;
  campaign?: any;
  linkout?: boolean;
}) => {
  const [modal, setModal] = useState<boolean>(false);
  const { selectedOrg } = useAppState().organization;
  const { setGlobalLinkProductAndOpenModal } = useActions().link;
  const flags = useFlags();
  let displayPrice;
  const price = parseFloat(product.product_price);
  const sale_price = parseFloat(product.sale_price);
  if (price > 0) {
    if (price > sale_price) {
      const percentSale = formatValue(
        ((price - sale_price) / price) * 100,
        'percent',
        'sale_percentage',
      );
      displayPrice = (
        <div className="flex flex-col sm:flex-row text-xs">
          <div className="line-through text-text-violet-300 sm:pr-2">
            ${product.product_price}
          </div>
          <div data-cy="sale-display">{`$${product.sale_price} (${percentSale} off)`}</div>
        </div>
      );
    } else {
      displayPrice = (
        <div className={classNames('text-xs')}>${product.product_price}</div>
      );
    }
  }

  const productItem = (
    <>
      <div
        className={classNames('overflow-hidden rounded-lg m-2', imageSize, {
          'w-32 h-32 sm:h-52 sm:w-52': !imageSize,
        })}
      >
        {product.gift_available && type === 'campaign' && (
          <div
            className={
              'absolute top-2 left-2 w-6 h-6 flex items-center bg-violet-500 rounded-full'
            }
          >
            <Icon iconName="gift" color="white" className="mx-auto" />
          </div>
        )}
        <ImageWithFallback
          src={product.image_url}
          alt="product image"
          fallBack={fallbackImage}
          className="h-full w-full object-contain rounded-lg"
          loggingInfo={{
            component:
              'apps/howl-web/src/components/publisher/campaign-center/product.tsx',
            productData: product,
          }}
        />
      </div>
      <div
        className={classNames(
          'space-y-2 py-2 text-xs flex flex-col justify-between h-28',
          textSize,
          {
            'w-38 sm:w-52': !textSize,
            'px-2': type !== 'campaigns',
          },
        )}
      >
        <div>
          {campaign && (
            <div
              data-cy="product-brand"
              className={classNames('text-black', {
                'px-2': type === 'campaigns',
              })}
            >
              {campaign.merch_name}
            </div>
          )}
          <div
            data-cy="product-name"
            className={classNames('text-black whitespace-normal line-clamp-3', {
              'px-2': type === 'campaigns',
            })}
          >
            {product.product_name}
          </div>
        </div>
        <div>
          {product?.num_variants && product?.num_variants > 1 ? (
            <div className="text-neutral-600">
              {product?.num_variants - 1} more option
              {product?.num_variants > 2 ? 's' : ''}
            </div>
          ) : null}
          {type === 'campaign'
            ? parseInt(product.product_price, 10) > 0 && displayPrice
            : type === 'campaigns'
            ? parseInt(product.product_price, 10) > 0 && (
                <div className={classNames('text-xs px-2')}>
                  ${product.product_price}
                </div>
              )
            : null}
        </div>
      </div>
    </>
  );

  return (
    <>
      {modal && (
        <LinkCreationModal
          modal={modal}
          fallBackImage={fallbackImage}
          product={product}
          setModal={setModal}
        />
      )}
      <div
        data-cy="product"
        className={classNames(
          'snap-center hover:no-underline text-black transition flex relative flex-col items-center justify-between rounded-lg bg-white text-sm ',
          className,
          {
            'mr-4 mb-4': !noMargin,
            'border border-gray-100 shadow-sm': bordered,
            'max-w-[150px] sm:max-w-none': type === 'campaign',
          },
        )}
      >
        {linkout ? (
          <a
            href={product.store_page_url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {productItem}
          </a>
        ) : (
          productItem
        )}
        {canMakeLink && (
          <div
            className={classNames(
              'px-2 py-1 text-xs border-gray-100 border-t',
              textSize,
              {
                'w-38 sm:w-52': !textSize,
              },
            )}
          >
            <button
              onClick={() => {
                if (
                  selectedOrg.publisher_type === 2 &&
                  flags['creatorGlobalLinkCreation']
                ) {
                  setGlobalLinkProductAndOpenModal({
                    value: {
                      imageUrl: product.image_url,
                      name: product.product_name,
                      merchant: merchName,
                      productUrl: product.store_page_url,
                      productPrice: product.product_price,
                    },
                    linkSource: 'creator_campaign_center',
                  });
                } else {
                  setModal(true);
                }
              }}
              data-cy="make-link-modal"
              data-ref="make-link-modal"
              className="cursor-pointer py-2 px-0  uppercase"
            >
              <Typography variant="label-2" className="text-violet-200">
                Make Link
              </Typography>
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default Product;
